import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone, Mail } from "lucide-react";
import { COMPANY_INFO } from "../data";
import { fetchCompanyInfo } from "../lib/cms";

export default function Header() {
  const [companyInfo, setCompanyInfo] = useState(COMPANY_INFO);
  const [isOpen, setIsOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    fetchCompanyInfo().then((info) => {
      if (info) setCompanyInfo(info);
    });
  }, []);

  const services = [
    { name: "Totalrenovering", path: "/totalrenovering" },
    { name: "Ombygning", path: "/ombygning" },
    { name: "Totalentreprise", path: "/totalentreprise" },
    { name: "Tilbygninger", path: "/ydelser/tilbygninger" },
    { name: "Træterrasser", path: "/ydelser/traeterrasser" },
    { name: "Døre og vinduer", path: "/ydelser/doere-og-vinduer" },
    { name: "Tagarbejde", path: "/ydelser/tagarbejde" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  const isActive = (path) => location.pathname === path;
  const isServiceActive = services.some((s) => location.pathname === s.path);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-primary-200/80 bg-white/95 backdrop-blur-md">
      {/* Top bar with contact info */}
      <div className="bg-primary-900 text-primary-100 py-2 px-4 sm:px-6 lg:px-8 text-xs font-medium">
        <div className="mx-auto flex max-w-7xl items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-brand-500 animate-pulse" />
              {companyInfo.experience}
            </span>
            <span className="hidden sm:inline-block text-primary-400">|</span>
            <span className="hidden sm:inline-block">Aarhus Syd, Højbjerg, Hasselager & Viby</span>
          </div>
          <div className="flex items-center gap-4">
            <a
              href={`tel:${companyInfo.phoneRaw}`}
              className="inline-flex items-center gap-2 rounded-full border border-primary-300 bg-white px-4 py-2 text-xs font-semibold text-primary-800 transition-colors duration-200 hover:border-brand-500 hover:bg-brand-50"
            >
              <Phone className="h-3.5 w-3.5 text-brand-600" />
              Ring {companyInfo.phone}
            </a>
            <span className="text-primary-400">|</span>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-1 hover:text-brand-400 transition-colors duration-200 whitespace-nowrap"
            >
              <Mail className="h-3.5 w-3.5 text-brand-500" />
              {companyInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="mx-auto h-[100px] max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-full items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={companyInfo.logo}
              alt={companyInfo.name || "Sehested Tømrerfirma"}
              className="h-[85px] w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation (>= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive("/") ? "text-brand-600" : "text-primary-700 hover:text-brand-600"
              }`}
            >
              Hjem
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors duration-200 focus:outline-none cursor-pointer ${
                  isServiceActive || isDropdownOpen ? "text-brand-600" : "text-primary-700 hover:text-brand-600"
                }`}
              >
                Ydelser
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`} />
              </button>

              {isDropdownOpen && (
                <div className="absolute left-0 mt-3 w-64 rounded-xl border border-primary-200 bg-white p-2 shadow-lg ring-1 ring-black/5 animate-in fade-in slide-in-from-top-2 duration-200">
                  {services.map((service) => (
                    <Link
                      key={service.path}
                      to={service.path}
                      className={`block rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                        isActive(service.path)
                          ? "bg-brand-50 text-brand-700 font-semibold"
                          : "text-primary-700 hover:bg-primary-50 hover:text-brand-600"
                      }`}
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/tidligere-projekter"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive("/tidligere-projekter") ? "text-brand-600" : "text-primary-700 hover:text-brand-600"
              }`}
            >
              Tidligere projekter
            </Link>

            <Link
              to="/galleri"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive("/galleri") ? "text-brand-600" : "text-primary-700 hover:text-brand-600"
              }`}
            >
              Galleri
            </Link>


            <Link
              to="/kontakt"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive("/kontakt") ? "text-brand-600" : "text-primary-700 hover:text-brand-600"
              }`}
            >
              Kontakt
            </Link>
          </nav>

          {/* CTA Button (>= 1200px) */}
          <div className="hidden min-[1200px]:block">
            <Link
              to="/kontakt"
              className="rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
            >
              Få et tilbud
            </Link>
          </div>

          {/* Mobile Menu Button (< 1200px) */}
          <div className="flex min-[1200px]:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex items-center justify-center rounded-lg p-2.5 text-primary-700 hover:bg-primary-100 hover:text-primary-900 focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer"
              aria-expanded="false"
            >
              <span className="sr-only">Åbn menu</span>
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Panel (< 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-primary-200 bg-white shadow-inner animate-in slide-in-from-top duration-200 max-h-[calc(100svh-100px)] overflow-y-auto overscroll-contain">
          <div className="flex min-h-full flex-col space-y-1 px-4 py-4 pb-8">
            <Link
              to="/"
              className={`block rounded-lg px-4 py-3 text-base font-semibold ${
                isActive("/") ? "bg-brand-50 text-brand-700" : "text-primary-800 hover:bg-primary-50 hover:text-brand-600"
              }`}
            >
              Hjem
            </Link>

            {/* Services Group */}
            <div className="space-y-1">
              <div className="px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-400">
                Vores ydelser
              </div>
              {services.map((service) => (
                <Link
                  key={service.path}
                  to={service.path}
                  className={`block rounded-lg pl-8 pr-4 py-2.5 text-sm font-medium ${
                    isActive(service.path)
                      ? "bg-brand-50/70 text-brand-700 font-semibold"
                      : "text-primary-700 hover:bg-primary-50 hover:text-brand-600"
                  }`}
                >
                  {service.name}
                </Link>
              ))}
            </div>

            <Link
              to="/tidligere-projekter"
              className={`block rounded-lg px-4 py-3 text-base font-semibold ${
                isActive("/tidligere-projekter") ? "bg-brand-50 text-brand-700" : "text-primary-800 hover:bg-primary-50 hover:text-brand-600"
              }`}
            >
              Tidligere projekter
            </Link>

            <Link
              to="/galleri"
              className={`block rounded-lg px-4 py-3 text-base font-semibold ${
                isActive("/galleri") ? "bg-brand-50 text-brand-700" : "text-primary-800 hover:bg-primary-50 hover:text-brand-600"
              }`}
            >
              Galleri
            </Link>


            <Link
              to="/kontakt"
              className={`block rounded-lg px-4 py-3 text-base font-semibold ${
                isActive("/kontakt") ? "bg-brand-50 text-brand-700" : "text-primary-800 hover:bg-primary-50 hover:text-brand-600"
              }`}
            >
              Kontakt
            </Link>

            <div className="pt-4 border-t border-primary-100 mt-4">
              <Link
                to="/kontakt"
                className="block w-full text-center rounded-lg bg-brand-600 px-4 py-3 text-base font-semibold text-white shadow-sm hover:bg-brand-700"
              >
                Få et tilbud
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
